import { Box, Button, Checkbox, CircularProgress, FormControlLabel, LinearProgress, Typography } from '@mui/material';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import RefreshIcon from '@mui/icons-material/Refresh';

const STAGE_LABELS = { xmp: 'XMP', cover: 'Cover' };

// Compact status/action indicator only — long text (dimension summary, error
// messages) renders full-width below the row instead, so it never has to
// compete for space with this narrow column (this overlap bug was already hit
// and fixed once for the single-stage cover list — same fix applies here).
function StageAction({ stage, onDownloadCover, onDownloadXmp }) {
  switch (stage.status) {
    case 'skipped':
      return <Typography variant="body2" color="text.disabled">Skipped</Typography>;

    case 'queued':
      return <Typography variant="body2" color="text.secondary">Queued</Typography>;

    case 'uploading':
      return (
        <Box sx={{ minWidth: 140 }}>
          <Typography variant="body2" sx={{ mb: 0.5 }}>{`Uploading… ${stage.progressPct}%`}</Typography>
          <LinearProgress variant="determinate" value={stage.progressPct} />
        </Box>
      );

    case 'processing':
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CircularProgress size={18} color="success" />
          <Typography variant="body2">Processing…</Typography>
        </Box>
      );

    case 'done':
      return onDownloadCover ? (
        <Button variant="contained" color="success" size="small" startIcon={<CloudDownloadIcon />} onClick={onDownloadCover}>
          Download
        </Button>
      ) : (
        <Button variant="outlined" size="small" startIcon={<CloudDownloadIcon />} onClick={onDownloadXmp}>
          Download
        </Button>
      );

    case 'error':
      return <Typography variant="body2" color="error">Failed</Typography>;

    default:
      return null;
  }
}

function DimensionSummary({ dimensions, resample }) {
  if (!dimensions) return null;
  const {
    contentWidthMm, contentHeightMm, coverWidthPx, coverHeightPx,
    contentAspectRatio, coverAspectRatio, diffPercent, matches,
  } = dimensions;
  return (
    <Typography variant="caption" color={matches ? 'text.secondary' : 'error'} sx={{ display: 'block', ml: 4.5 }}>
      {`PDF ${contentWidthMm.toFixed(1)}×${contentHeightMm.toFixed(1)}mm (${contentAspectRatio.toFixed(2)}) · `
        + `Cover ${coverWidthPx}×${coverHeightPx}px (${coverAspectRatio.toFixed(2)}) · Δ${diffPercent.toFixed(2)}%`
        + (resample?.downsampled
          ? ` · Downsampled to ${resample.embeddedWidthPx}×${resample.embeddedHeightPx}px (${resample.maxDpi} DPI cap)`
          : '')}
    </Typography>
  );
}

function getRowBackgroundColor(entry) {
  const relevant = Object.entries(entry.stages)
    .filter(([name]) => entry.selectedStages[name])
    .map(([, s]) => s.status);
  if (relevant.some((s) => s === 'error')) return 'rgba(244,67,54,0.05)';
  if (relevant.length > 0 && relevant.every((s) => s === 'done')) return 'rgba(76,175,80,0.05)';
  return 'white';
}

function PipelineFileListRow({ entry, isLast, onDownloadCover, onToggleStage, onRetry }) {
  const { xmp, cover } = entry.stages;
  const hasError = xmp.status === 'error' || cover.status === 'error';
  // Toggling only ever affects what the *next* Retry does, never a request already
  // in flight — so lock the checkbox exactly while that stage is actively running,
  // and leave it editable everywhere else, including after it's done/errored
  // (files auto-start immediately, so terminal states are reached almost right
  // away — locking them too would mean the checkbox could basically never be used).
  const checkboxLockedFor = (stage) => stage.status === 'uploading' || stage.status === 'processing';

  return (
    <Box sx={{ p: 2, borderBottom: isLast ? 'none' : '1px solid #eee', backgroundColor: getRowBackgroundColor(entry) }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0, flex: 1 }}>
          <PictureAsPdfIcon sx={{ color: 'text.secondary', flexShrink: 0 }} />
          <Typography noWrap title={entry.filename}>{entry.filename}</Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: 3, alignItems: 'center', flexShrink: 0 }}>
          {['xmp', 'cover'].map((stageName) => (
            <Box key={stageName} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5, minWidth: 150 }}>
              <FormControlLabel
                sx={{ mr: 0 }}
                control={
                  <Checkbox
                    size="small"
                    checked={entry.selectedStages[stageName]}
                    disabled={checkboxLockedFor(entry.stages[stageName])}
                    onChange={(e) => onToggleStage(entry, stageName, e.target.checked)}
                  />
                }
                label={<Typography variant="caption" color="text.secondary">{STAGE_LABELS[stageName]}</Typography>}
              />
              <StageAction
                stage={entry.stages[stageName]}
                onDownloadCover={stageName === 'cover' ? () => onDownloadCover(entry) : undefined}
                onDownloadXmp={stageName === 'xmp' ? () => window.open(entry.stages.xmp.s3Url, '_blank', 'noopener') : undefined}
              />
            </Box>
          ))}

          {hasError && (
            <Button variant="outlined" color="error" size="small" startIcon={<RefreshIcon />} onClick={() => onRetry(entry)}>
              Retry
            </Button>
          )}
        </Box>
      </Box>

      <DimensionSummary dimensions={cover.dimensionInfo} resample={cover.resampleInfo} />

      {xmp.status === 'error' && xmp.error && (
        <Typography variant="caption" color="error" sx={{ display: 'block', ml: 4.5, mt: 0.5 }}>
          {`XMP: ${xmp.error}`}
        </Typography>
      )}
      {cover.status === 'error' && cover.error && (
        <Typography variant="caption" color="error" sx={{ display: 'block', ml: 4.5, mt: 0.5 }}>
          {`Cover: ${cover.error}`}
        </Typography>
      )}
    </Box>
  );
}

function PipelineFileList({ files, onDownloadCover, onToggleStage, onRetry }) {
  if (files.length === 0) return null;

  return (
    <Box sx={{ border: '1px solid #ddd', borderRadius: 1, mb: 3 }}>
      {files.map((entry, index) => (
        <PipelineFileListRow
          key={entry.id}
          entry={entry}
          isLast={index === files.length - 1}
          onDownloadCover={onDownloadCover}
          onToggleStage={onToggleStage}
          onRetry={onRetry}
        />
      ))}
    </Box>
  );
}

export default PipelineFileList;
